<template>
    <div class="CreateEntityForm">
        <component
            :is="getComponent(field.type.$type)"
            v-for="field in standardFields"
            :key="field.path"
            class="CreateEntityForm__field"
            :form-id="formId"
            :field-path="field.path"
        />
        <template v-if="exceptionFields.length > 0">
            <MigratedComplianceSection
                class="CreateEntityForm__field"
                :count="migratedRequirements.length"
                :has-error="hasExceptionError"
            >
                <div
                    v-for="field in exceptionFields"
                    :key="field.path"
                    class="CreateEntityForm__exceptionRow"
                >
                    <div class="CreateEntityForm__exceptionName" aria-hidden="true">
                        <span class="CreateEntityForm__exceptionTitle">{{ field.label }}</span>
                        <span class="CreateEntityForm__exceptionHint">Valid until</span>
                    </div>
                    <component
                        :is="getComponent(field.type.$type)"
                        class="CreateEntityForm__exceptionInput"
                        :form-id="formId"
                        :field-path="field.path"
                    />
                </div>
            </MigratedComplianceSection>
            <MigratedComplianceSummary
                class="CreateEntityForm__field"
                :items="migratedRequirements"
                :expired="expiredRequirements"
            />
        </template>
    </div>
</template>

<style lang="scss">
    // stylelint-disable selector-max-combinators, selector-max-compound-selectors
    .CreateEntityForm {
        &__field + &__field {
            margin-top: 16px;
        }

        &__exceptionRow {
            align-items: center;
            display: grid;
            gap: 12px;
            grid-template-columns: minmax(0, 1fr) 150px;
        }

        &__exceptionName {
            display: flex;
            flex-direction: column;
            gap: 2px;
        }

        &__exceptionTitle {
            color: var(--eidQualityBlack);
            font-size: var(--text-body-size, 14px);
        }

        &__exceptionHint {
            color: var(--eidTechGray);
            font-size: 12px;
        }

        // The date input's own label is kept for screen readers and hidden visually, because
        // the row shows the requirement name beside the input instead of above it.
        &__exceptionInput .DefaultLayout__label {
            border: 0;
            clip: rect(0, 0, 0, 0);
            height: 1px;
            margin: -1px;
            overflow: hidden;
            padding: 0;
            position: absolute;
            white-space: nowrap;
            width: 1px;
        }
    }
</style>

<script lang="ts">
    import { defineAsyncComponent, defineComponent, PropType } from 'vue';

    import {
        getExpiredRequirements,
        getMigratedRequirements,
        MigratedRequirement,
        RequirementLabels,
    } from '@/components/migrated-compliance-section/migratedCompliance';
    import MigratedComplianceSection from '@/components/migrated-compliance-section/MigratedComplianceSection.vue';
    import MigratedComplianceSummary from '@/components/migrated-compliance-section/MigratedComplianceSummary.vue';
    import { FieldMeta } from '@/modules/dynamic-form/models/FieldMeta.model';
    import { FieldType } from '@/modules/dynamic-form/models/FieldType.model';
    import { useDynamicFormStore } from '@/modules/dynamic-form/store';
    import { getFieldDataFromUnifiedRecord } from '@/modules/dynamic-form/utils/form-field-helpers/formFieldHelpers';
    import { getControlComponents, ComponentResolver } from '@/modules/dynamic-form/utils/get-control-components/getControlComponents';

    const EXCEPTION_PATH_PREFIX = 'exceptions.';

    const customComponentsMap: Partial<Record<FieldType, ComponentResolver>> = {
        Address: defineAsyncComponent(() => import('../../create-entity-form-inputs/address/AddressInput.vue')),
        Collateral: defineAsyncComponent(() => import('../../create-entity-form-inputs/collateral/CollateralInput.vue')),
    };

    export default defineComponent({
        name: 'CreateEntityForm',
        components: { MigratedComplianceSection, MigratedComplianceSummary },
        props: {
            formId: {
                type: String as PropType<string>,
                required: true,
            },
            fields: {
                type: Array as PropType<FieldMeta[]>,
                default: (): FieldMeta[] => [],
            },
        },
        computed: {
            standardFields(): FieldMeta[] {
                return this.fields.filter((field) => !this.isExceptionField(field));
            },
            exceptionFields(): FieldMeta[] {
                return this.fields.filter((field) => this.isExceptionField(field));
            },
            exceptionTypes(): string[] {
                return this.exceptionFields.map((field) => field.path.slice(EXCEPTION_PATH_PREFIX.length));
            },
            exceptionDates(): Record<string, string | undefined> {
                const values = useDynamicFormStore().forms[this.formId]?.values ?? {};

                return Object.fromEntries(
                    this.exceptionTypes.map((type) => [ type, values[`${EXCEPTION_PATH_PREFIX}${type}`] ]),
                );
            },
            // The rows' own labels, so the summary names a requirement exactly as its row does.
            exceptionLabels(): RequirementLabels {
                return Object.fromEntries(this.exceptionFields.map((field, index) => [
                    this.exceptionTypes[index],
                    { defaultLabel: field.label },
                ]));
            },
            migratedRequirements(): MigratedRequirement[] {
                return getMigratedRequirements(this.exceptionDates, this.exceptionTypes, this.exceptionLabels);
            },
            expiredRequirements(): MigratedRequirement[] {
                return getExpiredRequirements(this.exceptionDates, this.exceptionTypes, this.exceptionLabels);
            },
            hasExceptionError(): boolean {
                const form = useDynamicFormStore().forms[this.formId];

                return Boolean(form?.showErrors) && this.exceptionFields.some(
                    (field) => Boolean(getFieldDataFromUnifiedRecord<string>(form.formErrors, field.path)),
                );
            },
        },
        methods: {
            getComponent(type: FieldType) {
                return getControlComponents(type, customComponentsMap);
            },
            isExceptionField(field: FieldMeta): boolean {
                return field.path.startsWith(EXCEPTION_PATH_PREFIX);
            },
        },
    });
</script>
